import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PrimaryActionButton, SecondaryActionButton, StonePanel } from '@aztec-realms/ui';
import {
  loadPushState,
  registerPushDevice,
  removePushDevice,
  savePushPreferences,
  selectPushDevice,
  testPushDevice,
  type PushPreferences,
  type PushState,
} from './data/push-api';
import {
  bindPushInstallation,
  currentPushBinding,
  pushCapability,
  subscribePushBrowser,
  unbindPushInstallation,
} from './data/push-installation';
import './push-settings.css';

const timeLabel = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
const minuteValue = (value: string) => {
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
};

export function PushSettings({
  ownerId,
  online,
  interactionLocked = false,
}: {
  ownerId?: string;
  online: boolean;
  interactionLocked?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <StonePanel className="settings-card push-settings">
      <span className="realm-screen__eyebrow">Your attention, your choice</span>
      <h2>Notifications</h2>
      <p>
        Your inbox keeps the thanks you receive. Optional device alerts never change your progress.
      </p>
      {!ownerId ? (
        <p>Notification preferences are available in your online account.</p>
      ) : (
        <>
          <SecondaryActionButton
            disabled={interactionLocked}
            onClick={() => setOpen(!open)}
            aria-expanded={open}
          >
            {open ? 'Close notification settings' : 'Manage notifications'}
          </SecondaryActionButton>
          {interactionLocked && <p>Manage notifications when safely stopped.</p>}
          {open && !interactionLocked && (
            <PushSettingsContent key={ownerId} ownerId={ownerId} online={online} />
          )}
        </>
      )}
    </StonePanel>
  );
}

function PushSettingsContent({ ownerId, online }: { ownerId: string; online: boolean }) {
  const client = useQueryClient();
  const key = ['push-settings', ownerId] as const;
  const state = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => loadPushState(signal),
    enabled: online,
    retry: false,
    gcTime: 0,
  });
  const [permission, setPermission] = useState(pushCapability);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [deviceLabel, setDeviceLabel] = useState('This browser');
  const active = useRef(true);
  const inFlight = useRef(false);
  useEffect(() => {
    active.current = true;
    const refreshPermission = () => setPermission(pushCapability());
    window.addEventListener('focus', refreshPermission);
    return () => {
      active.current = false;
      window.removeEventListener('focus', refreshPermission);
    };
  }, []);

  async function act(work: () => Promise<PushState | void>, success: string) {
    if (inFlight.current || !online) return;
    inFlight.current = true;
    setBusy(true);
    setMessage('');
    try {
      const updated = await work();
      if (!active.current) return;
      if (updated) client.setQueryData(key, updated);
      setMessage(success);
    } catch (error) {
      if (active.current)
        setMessage(error instanceof Error ? error.message : 'Reconnect and try again.');
    } finally {
      inFlight.current = false;
      if (active.current) {
        setBusy(false);
        setPermission(pushCapability());
      }
    }
  }

  if (!online)
    return (
      <p role="status">
        You’re offline. Your inbox and push preferences will refresh when connected.
      </p>
    );
  if (state.isPending) return <p role="status">Loading notification preferences…</p>;
  if (state.isError || !state.data)
    return (
      <div role="alert">
        <p>Notification preferences are unavailable.</p>
        <SecondaryActionButton onClick={() => void state.refetch()}>
          Try again
        </SecondaryActionButton>
      </div>
    );
  const data = state.data;
  const preferences: PushPreferences = {
    enabled: data.enabled,
    social_thanks: data.social_thanks,
    timezone: data.timezone ?? '',
    quiet_start_minute: data.quiet_start_minute,
    quiet_end_minute: data.quiet_end_minute,
  };
  const supported = permission !== 'unsupported' && permission !== 'denied';
  const local = currentPushBinding();
  const currentDevice = local?.ownerId === ownerId ? local.deviceId : undefined;
  return (
    <div className="push-settings__content">
      <p role="status">
        {!data.available
          ? 'Device alerts are not available yet. Your inbox remains available.'
          : permission === 'unsupported'
            ? 'This browser cannot receive device alerts. Use your inbox; on supported Apple devices, install the app on the Home Screen first.'
            : permission === 'denied'
              ? 'Notifications are blocked in your browser. You can change this in browser settings. Your inbox remains available.'
              : data.enabled
                ? 'Account alerts are enabled. Only your selected device receives them.'
                : 'Device alerts are paused. Your inbox remains available.'}
      </p>
      <p>
        Only earned thank-you alerts are available. Reminders and other categories are not enabled.
        Alerts use generic lock-screen text, never names or locations.
      </p>
      <QuietHours
        key={`${data.timezone}:${data.quiet_start_minute}:${data.quiet_end_minute}`}
        data={data}
        busy={busy}
        save={(values) =>
          void act(() => savePushPreferences({ ...preferences, ...values }), 'Quiet hours saved.')
        }
      />
      <label className="settings-option">
        <input
          type="checkbox"
          checked={data.social_thanks}
          disabled={busy || !data.timezone}
          onChange={(event) =>
            void act(
              () => savePushPreferences({ ...preferences, social_thanks: event.target.checked }),
              'Thank-you preference saved.',
            )
          }
        />
        <span>Earned social thanks</span>
      </label>
      <label className="push-settings__device-label">
        Device name
        <input
          value={deviceLabel}
          maxLength={64}
          disabled={busy}
          onChange={(event) => setDeviceLabel(event.target.value)}
          autoComplete="off"
        />
      </label>
      <div className="push-settings__actions">
        <PrimaryActionButton
          disabled={
            busy ||
            !data.available ||
            !supported ||
            !data.timezone ||
            !data.social_thanks ||
            !deviceLabel.trim()
          }
          onClick={() => {
            if (inFlight.current || !online) return;
            // Call the permission API directly inside this voluntary gesture, before any await.
            const permissionRequest =
              permission === 'default'
                ? Notification.requestPermission()
                : Promise.resolve(permission);
            void act(async () => {
              const allowed = await permissionRequest;
              if (!active.current) return;
              setPermission(allowed);
              if (allowed !== 'granted')
                throw new Error('Device alerts remain off. You can keep using your inbox.');
              if (!data.application_server_key)
                throw new Error('Device alerts are unavailable. Your inbox remains available.');
              const subscription = await subscribePushBrowser(data.application_server_key);
              try {
                if (!active.current) {
                  await subscription.unsubscribe();
                  return;
                }
                const registered = await registerPushDevice(subscription, deviceLabel.trim());
                if (!active.current) {
                  await subscription.unsubscribe();
                  return;
                }
                if (!registered.selected_device_id)
                  throw new Error('This device was not selected. Try again.');
                await bindPushInstallation(ownerId, registered.selected_device_id);
                if (!active.current) {
                  await unbindPushInstallation(false);
                  return;
                }
                return await savePushPreferences({ ...preferences, enabled: true });
              } catch (error) {
                if (active.current) await unbindPushInstallation(false);
                await subscription.unsubscribe();
                throw error;
              }
            }, 'Alerts enabled on this device. Delivery respects quiet hours and active Journeys.');
          }}
        >
          Enable on this device
        </PrimaryActionButton>
        <SecondaryActionButton
          disabled={busy || !data.enabled}
          onClick={() =>
            void act(
              () => savePushPreferences({ ...preferences, enabled: false }),
              'All device alerts paused. Your inbox stays available.',
            )
          }
        >
          Pause all device alerts
        </SecondaryActionButton>
        <SecondaryActionButton
          disabled={busy || !data.available || !data.enabled || !data.selected_device_id}
          onClick={() =>
            void act(async () => {
              const result = await testPushDevice();
              if (result.status === 'unavailable')
                throw new Error('Device alerts are unavailable. No test was sent.');
              if (result.status === 'deferred')
                throw new Error(
                  'Test deferred by quiet hours, an active Journey or the alert limit.',
                );
            }, 'Test queued for your selected device. Delivery is not yet confirmed.')
          }
        >
          Request test notification
        </SecondaryActionButton>
      </div>
      <h3>Your devices ({data.devices.length}/5)</h3>
      {data.devices.length === 0 ? (
        <p>No devices registered.</p>
      ) : (
        <ul className="push-settings__devices">
          {data.devices.map((device) => (
            <li key={device.id}>
              <span>
                <strong>{device.label}</strong>
                {device.id === currentDevice ? ' · this device' : ''}
                {device.selected ? ' · receives alerts' : ''}
                <small>Added {new Date(device.created_at).toLocaleDateString()}</small>
              </span>
              <div className="push-settings__actions">
                <SecondaryActionButton
                  disabled={busy || device.selected}
                  onClick={() =>
                    void act(() => selectPushDevice(device.id), 'Selected device updated.')
                  }
                >
                  Use {device.label}
                </SecondaryActionButton>
                <SecondaryActionButton
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      if (device.id === currentDevice) await unbindPushInstallation(false);
                      return removePushDevice(device.id);
                    }, 'Device removed. It will not receive new alerts.')
                  }
                >
                  Remove {device.label}
                </SecondaryActionButton>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p>
        Quiet hours apply to all alerts. At most three engagement alerts per 24 hours across push
        and email. Alerts wait during an active Journey; already accepted messages may still arrive.
      </p>
      {message && (
        <p role="status" className="push-settings__message">
          {message}
        </p>
      )}
    </div>
  );
}

function QuietHours({
  data,
  busy,
  save,
}: {
  data: PushState;
  busy: boolean;
  save: (
    values: Pick<PushPreferences, 'timezone' | 'quiet_start_minute' | 'quiet_end_minute'>,
  ) => void;
}) {
  const [timezone, setTimezone] = useState(
    data.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
  );
  const [start, setStart] = useState(timeLabel(data.quiet_start_minute));
  const [end, setEnd] = useState(timeLabel(data.quiet_end_minute));
  const [error, setError] = useState('');
  return (
    <form
      className="push-settings__quiet"
      onSubmit={(event) => {
        event.preventDefault();
        try {
          new Intl.DateTimeFormat('en', { timeZone: timezone });
        } catch {
          setError('Use a valid IANA timezone, such as Europe/Amsterdam.');
          return;
        }
        if (!timezone || !start || !end || start === end) {
          setError('Choose a timezone and different start and end times.');
          return;
        }
        setError('');
        save({
          timezone,
          quiet_start_minute: minuteValue(start),
          quiet_end_minute: minuteValue(end),
        });
      }}
    >
      <h3>Quiet hours</h3>
      <p>
        {data.timezone
          ? 'Saved timezone and quiet hours apply across your devices.'
          : 'Confirm your timezone before enabling alerts. This suggestion comes from your browser, not GPS.'}
      </p>
      <label>
        Timezone
        <input
          required
          maxLength={64}
          value={timezone}
          onChange={(event) => setTimezone(event.target.value)}
          disabled={busy}
          autoComplete="off"
        />
      </label>
      <div className="push-settings__times">
        <label>
          Quiet from
          <input
            required
            type="time"
            value={start}
            onChange={(event) => setStart(event.target.value)}
            disabled={busy}
          />
        </label>
        <label>
          Quiet until
          <input
            required
            type="time"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
            disabled={busy}
          />
        </label>
      </div>
      <SecondaryActionButton type="submit" disabled={busy}>
        Confirm timezone and quiet hours
      </SecondaryActionButton>
      {error && <p role="alert">{error}</p>}
    </form>
  );
}
